import { useState, useEffect, useCallback, useRef } from "react";
import { useAdsDB } from "@/hooks/useAdsDB";

interface AdSlotDisplayProps {
  position: string;
  className?: string;
}

// Min-height map per position to prevent layout shift (CLS)
const minHeightMap: Record<string, string> = {
  header_banner: "min-h-[90px] md:min-h-[250px]",
  top_leaderboard: "min-h-[50px] md:min-h-[90px]",
  sidebar_top: "min-h-[250px]",
  sidebar_large: "min-h-[300px] md:min-h-[600px]",
  in_article_p2: "min-h-[100px] md:min-h-[250px]",
  in_article_p5: "min-h-[100px] md:min-h-[250px]",
  homepage_section: "min-h-[90px] md:min-h-[250px]",
  category_page: "min-h-[250px]",
  article_bottom: "min-h-[90px] md:min-h-[250px]",
  footer_banner: "min-h-[50px] md:min-h-[90px]",
  mobile_top: "min-h-[50px] md:min-h-[100px]",
  mobile_in_article: "min-h-[100px]",
  mobile_between_cards: "min-h-[100px]",
  mobile_sticky: "min-h-[50px]",
  hero_grid_bottom: "min-h-[120px] md:min-h-[180px]",
  mobile_above_authors: "min-h-[50px] md:hidden",
  mobile_after_hero: "min-h-[50px] md:hidden",
  mobile_between_sections: "min-h-[50px] md:hidden",
  mobile_between_cards_cat: "min-h-[50px] md:hidden",
  mobile_before_footer: "min-h-[50px] md:hidden",
  mobile_article_mid: "min-h-[100px] md:hidden",
  mobile_between_cards_home: "min-h-[50px] md:hidden",
  mobile_search_top: "min-h-[50px] md:hidden",
  mobile_search_between: "min-h-[50px] md:hidden",
  mobile_search_footer: "min-h-[50px] md:hidden",
};

// AdSense script injection flag (per-session)
let adsenseScriptInjected = false;

function injectAdSenseScript() {
  if (adsenseScriptInjected || typeof document === "undefined") return;
  const script = document.createElement("script");
  script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
  script.async = true;
  script.crossOrigin = "anonymous";
  document.head.appendChild(script);
  adsenseScriptInjected = true;
}

export default function AdSlotDisplay({ position, className = "" }: AdSlotDisplayProps) {
  const { placements } = useAdsDB();
  const ad = placements.find((p) => p.position === position && p.isActive);
  const adRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const minH = minHeightMap[position] || "min-h-[90px]";

  // Auto-inject AdSense script when an AdSense ad is visible
  useEffect(() => {
    if (ad?.adType === "adsense") {
      injectAdSenseScript();
      // Give script time to load
      const t = setTimeout(() => setScriptLoaded(true), 2000);
      return () => clearTimeout(t);
    }
  }, [ad?.adType, ad?.id]);

  if (!ad) {
    return null;
  }

  return (
    <div
      className={`w-full ${minH} ${className} overflow-hidden`}
      style={{ contain: "layout size" }}
      aria-label="Werbung"
      ref={adRef}
    >
      <div className="flex flex-col items-center w-full h-full">
        <span className="text-[10px] text-hj-muted uppercase tracking-widest mb-1 font-body select-none">
          Werbung
        </span>

        <div className="w-full flex-1 flex items-center justify-center">
          {ad.adType === "image_banner" && ad.imageUrl ? (
            <a
              href={ad.linkUrl || "#"}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              className="block w-full h-full"
              aria-label={ad.imageAlt || ad.name}
            >
              <img
                src={ad.imageUrl}
                alt={ad.imageAlt || ad.name}
                loading="lazy"
                width={ad.width}
                height={ad.height}
                className="mx-auto w-full h-full object-contain rounded"
                style={{ maxHeight: `${ad.height}px` }}
              />
            </a>
          ) : ad.adType === "adsense" ? (
            <div className="w-full h-full flex items-center justify-center">
              {scriptLoaded ? (
                <ins
                  className="adsbygoogle"
                  style={{
                    display: "block",
                    width: "100%",
                    maxWidth: `${ad.width}px`,
                    minHeight: `${Math.min(ad.height, 250)}px`,
                  }}
                  data-ad-client={ad.adsensePublisherId || ""}
                  data-ad-slot={ad.adsenseSlotId || ""}
                  data-ad-format="auto"
                  data-full-width-responsive="true"
                />
              ) : (
                <div className="w-full bg-hj-gray-light/80 rounded flex items-center justify-center text-xs text-hj-muted font-body">
                  <div className="text-center px-4 py-6">
                    <i className="ri-advertisement-line text-3xl text-hj-gray-border mb-2 block" />
                    <span className="font-medium">Google AdSense</span>
                    <p className="text-[10px] mt-1 text-hj-gray">{ad.width}×{ad.height} • {ad.name}</p>
                  </div>
                </div>
              )}
            </div>
          ) : ad.adType === "custom_html" ? (
            <div
              className="w-full h-full bg-hj-gray-light/60 rounded flex items-center justify-center text-xs text-hj-muted font-body overflow-hidden"
              style={{ minHeight: `${Math.min(ad.height, 250)}px` }}
            >
              <div className="text-center px-4">
                <i className="ri-code-line text-2xl text-hj-gray-border mb-1 block" />
                <span>Custom HTML Ad</span>
                <p className="text-[10px] mt-1">{ad.width}×{ad.height} • {ad.name}</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}